/**
 *
 * @param documentId Document id whose session we you want to fetch
 *
 * @description Fetches the session based on the document when the document is pin protected
 */

import {
	Block,
	GOFBlockChanges,
	GOFBlockDataChanges,
	GOFBlockSection,
	GOFBundledProductChanges,
	GOFProductChanges,
	QuoteBlockData
} from "@/Contracts/ProposalBuilder/Block";
import { removeAlphaFromRgba, ZomentumStorageKeys } from "@/Contracts";
import { BlockType, cache } from "@/Contracts";
import { DocumentCurrencyConfigView } from "@/Contracts/common";
import { Template } from "@/Contracts/Templates";
import {
	Document,
	DocumentPaperSize,
	DocumentPayment,
	DocumentPaymentSummaryAndTerm,
	DocumentRecipient,
	DocumentType,
	InputField,
	InputFieldType,
	PaymentAmountType,
	PaymentMethod,
	PaymentStatus
} from "@/Contracts/Documents";

export const isGivenDocumentOrderForm = (
	currentActiveDocument: Document | Template | null
) => {
	const { document_type } = currentActiveDocument || {};
	return (
		document_type === DocumentType.GENERIC_ORDER_FORM ||
		document_type === DocumentType.SPECIFIC_ORDER_FORM
	);
};

export const isAssessmentOrQbrBlockUtil = (blockType: BlockType | null) => {
	if (
		blockType === BlockType.RISK_LISTING ||
		blockType === BlockType.HEALTH_STATUS ||
		blockType === BlockType.HEALTH_LEGEND ||
		blockType === BlockType.RISK_BAR ||
		blockType === BlockType.RISK_MATRIX
	) {
		return true;
	}
	return false;
};

// issue is (295.765).toFixed(2) give the result as "295.76", but it should be "295.77"
// so using this method to round the number to 2 decimal places
function roundNum(num: number, length: number) {
	return Math.round(num * Math.pow(10, length)) / Math.pow(10, length);
}

// This method gets payable amount of document, which doesn't include adjustment fees
export const getPayableAmount = (
	payment: DocumentPayment | DocumentPaymentSummaryAndTerm | null,
	grand: number
): number => {
	if (payment?.custom_amount != null || payment?.custom_amount != undefined)
		return payment?.custom_amount;
	if (
		payment?.custom_percentage != null ||
		payment?.custom_percentage != undefined
	)
		return roundNum(((grand ?? 0) * payment?.custom_percentage) / 100, 2);
	return grand;
};

export const getAdjustmentFeesCharged = (
	payment: DocumentPayment | null
): number | undefined => {
	if (!payment) return undefined;
	if (
		payment.status &&
		!(
			payment.status === PaymentStatus.Paid ||
			payment.status === PaymentStatus.Opt_Out
		)
	)
		return undefined;
	switch (payment.payment_method) {
		case PaymentMethod.CREDIT_CARD:
			return payment.payment_options?.credit_card?.total_adjustment_fees;
		case PaymentMethod.ACH:
			return payment.payment_options?.ach?.total_adjustment_fees;
		default:
			return undefined;
	}
};

export const getPaymentAmountType = (
	paymentSummaryAndTerm: DocumentPaymentSummaryAndTerm | null | undefined
): PaymentAmountType => {
	if (paymentSummaryAndTerm?.custom_amount != null) {
		return PaymentAmountType.CUSTOM_AMOUNT;
	}
	if (paymentSummaryAndTerm?.custom_percentage != null) {
		return PaymentAmountType.CUSTOM_PERCENTAGE;
	}
	return PaymentAmountType.GRAND;
};

export const convertHtmlElementToInputField = (
	element: HTMLElement | null,
	signers: DocumentRecipient[] = []
): InputField | null => {
	if (element === null || !element.dataset.fieldType) {
		return null;
	}
	const assignee = signers?.find(
		signer => signer.id === element.dataset.fieldAssignee
	)?.id;
	const inputField = new InputField();
	inputField.type = element.dataset.fieldType as InputFieldType;
	inputField.assignee = assignee || element.dataset.fieldAssignee || "";
	if (!inputField.assignee || inputField.assignee === "undefined") {
		// checking with undefined as string because from form the value is taken as undefined and stored as string
		inputField.assignee = undefined;
	}
	inputField.label = element.dataset.fieldLabel || "";
	inputField.placeholder = element.dataset.fieldPlaceholder || "";
	inputField.length = +(element.dataset.fieldLength || "10");
	inputField.isRequired = element.dataset.fieldIsrequired === "true";
	inputField.dataRole = element.dataset.fieldRole;
	inputField.external_id = element.dataset.fieldExternalid ?? "";
	return inputField;
};

export const getTag = (type: InputFieldType) => {
	if (type === InputFieldType.Text) {
		return "t";
	} else if (type === InputFieldType.Date) {
		return "d";
	} else {
		return "c";
	}
};

export interface ConstructInputTagInterface {
	inputField: InputField;
	forPdf: boolean;
	signers: DocumentRecipient[];
	parentTextBlockBackgroundColor: string;
	setPlaceholderForPdf: boolean;
	shouldAddLengthToPlaceholder?: boolean | null;
	id?: string | null;
	fieldId?: string | null;
}

export const constructInputTag = ({
	inputField,
	forPdf,
	signers,
	parentTextBlockBackgroundColor,
	setPlaceholderForPdf,
	id,
	fieldId
}: ConstructInputTagInterface): string => {
	const placeholder = inputField.placeholder || "";
	const emptySpaceLength = inputField.length - placeholder.length;
	const emptySpace =
		emptySpaceLength > 0
			? `${new Array(emptySpaceLength).fill(".").join("")}`
			: "";
	const emptySpaceSpan = `<span style="opacity: 0;">${emptySpace}</span>`;
	const tag = getTag(inputField.type);
	const index = signers.map(a => a.id).indexOf(inputField.assignee || "");
	const isRequired = inputField.isRequired;
	const tags = !!id
		? `[[#Z${id}`
		: `[[${tag}|${index}|p:0|r:${isRequired ? 1 : 0}|l:${
				inputField.length
		  }${inputField.external_id ? `|id:${inputField.external_id}` : ""}${
				fieldId ? `|fid:${fieldId}` : ""
		  }`;
	const excessLength = emptySpaceLength + placeholder.length - tags.length;
	const excessSpace =
		excessLength > 0 ? new Array(excessLength).fill("#").join("") : "";
	const placeholderSpace =
		emptySpaceLength - tags.length > 0
			? new Array(emptySpaceLength - tags.length).fill("#").join("")
			: "";
	const alphaRemovedColor = removeAlphaFromRgba(
		parentTextBlockBackgroundColor
	);
	const excessSpaceForInputFields = 3;
	const spaceNeeded =
		inputField.length > placeholder.length
			? inputField.length
			: placeholder.length;
	const inputFieldIdBasedSpaceHolder =
		spaceNeeded < tags.length
			? "#".repeat(
					tags.length - spaceNeeded < excessSpaceForInputFields
						? excessSpaceForInputFields
						: 0
			  )
			: "#".repeat(spaceNeeded - tags.length + excessSpaceForInputFields);
	const modifierTag = `<span style="letter-spacing: -3px; color: rgba(0, 0, 0, 0); font-family: 'Arial, Helvetica, sans-serif';">${tags}${excessSpace}]]</span>`;
	const str = `<span class="input-field-placeholder" style="padding: 0 4px 0 4px; ">${
		placeholder.length > 5 || tag !== "t"
			? placeholder
			: `<span style="position: absolute; inset-inline-start:4px; background: #ffedb4;">${placeholder}</span>${modifierTag}`
	}</span>${emptySpaceSpan}`;
	if (forPdf) {
		return `${
			inputField.label ? inputField.label + " " : ""
		} <span class="input-field-print" style="white-space: pre; font-family: 'Arial, Helvetica, sans-serif'; display: inline-block; color: ${alphaRemovedColor}; opacity:0.01; ${
			inputField.type === InputFieldType.CheckBox && "width: 1.4rem;"
		}">${tags}${
			!id
				? `${
						setPlaceholderForPdf
							? `|n:${placeholder}${placeholderSpace}`
							: excessSpace
				  }`
				: inputFieldIdBasedSpaceHolder
		}]]</span>`;
	} else {
		return `${
			inputField.label ? inputField.label + " " : ""
		}<span style="white-space: pre; background: #ffedb4; display: inline-block; white-space: pre; color: #7f7f7f; ${
			inputField.type === InputFieldType.CheckBox ? "width: 1.4rem;" : ""
		}">${str}</span>`;
	}
};

export const setGOFBlockData = (document: Document, block: Block) => {
	const { id: documentId, updated } = document;
	let existingData = cache.get(ZomentumStorageKeys.GOF_BLOCK_CACHE, true);
	let blockList: { [key: string]: GOFBlockChanges } = {};
	if (!existingData) {
		existingData = {};
	}
	if (existingData[documentId]) {
		blockList = existingData[documentId].blocks ?? {};
	} else {
		existingData[documentId] = { blocks: blockList };
	}
	const blockData = block?.data as QuoteBlockData;
	const sectionsData = blockData?.sections?.map(section => {
		const { selected_products, products } = section;
		const newProductsList = products.map(product => {
			const { quantity, bundled_products } = product;
			const newBundledProducts = bundled_products?.map(({ quantity }) => {
				return new GOFBundledProductChanges(quantity);
			});
			return new GOFProductChanges(quantity, newBundledProducts || []);
		});
		return new GOFBlockSection(selected_products, newProductsList || []);
	});

	const newChangedBlock: GOFBlockChanges = new GOFBlockChanges(
		block.id,
		new GOFBlockDataChanges(sectionsData, blockData?.tax, blockData?.total),
		block.updated
	);
	blockList[block.id] = newChangedBlock;
	existingData[documentId].blocks = blockList;
	existingData[documentId].updated = updated;
	cache.save(ZomentumStorageKeys.GOF_BLOCK_CACHE, existingData);
};

export const setGOFPaymentData = (
	documentId: string,
	payment: DocumentPayment
) => {
	const existingData = cache.get(ZomentumStorageKeys.GOF_BLOCK_CACHE, true);
	const documentData = existingData?.[documentId];
	if (documentData) {
		documentData.payment = payment;
		cache.save(ZomentumStorageKeys.GOF_BLOCK_CACHE, existingData);
	}
};

export const getGOFDocumentData = (documentId: string) => {
	const existingData = cache.get(ZomentumStorageKeys.GOF_BLOCK_CACHE, true);
	const blockList = existingData?.[documentId]?.blocks;
	if (blockList) {
		existingData[documentId].blocks = Object.values(blockList)?.map(
			(block: any) => {
				return { id: block.id, data: block.data };
			}
		);
		return existingData[documentId];
	}
	return null;
};

export const deleteGOFBlockData = (documentId: string) => {
	const existingData = cache.get(ZomentumStorageKeys.GOF_BLOCK_CACHE, true);
	delete existingData?.[documentId];
	cache.save(ZomentumStorageKeys.GOF_BLOCK_CACHE, existingData);
};

export const updateGOFDocumentData = (document: Document) => {
	const existingData = cache.get(ZomentumStorageKeys.GOF_BLOCK_CACHE, true);
	const documentData = existingData?.[document.id];
	const changedBlocks = documentData?.blocks;
	if (document.updated !== documentData?.updated) {
		return deleteGOFBlockData(document.id);
	}

	if (changedBlocks) {
		const newBlocksData = document?.revisions?.[0]?.blocks?.data;
		const isAnyBlockChanged = newBlocksData?.some(block => {
			const { updated, id } = block;
			const oldBlock = changedBlocks[id];
			return oldBlock && oldBlock.updated !== updated;
		});
		if (isAnyBlockChanged) {
			return deleteGOFBlockData(document.id);
		}
		newBlocksData?.forEach(block => {
			const changedBlockData = changedBlocks[block.id]
				?.data as GOFBlockDataChanges;
			const blockData = block?.data as QuoteBlockData;
			if (changedBlockData) {
				blockData.tax = changedBlockData.tax;
				blockData.total = changedBlockData.total;
			}
			if (changedBlockData?.sections && blockData?.sections) {
				blockData.sections.forEach((section, index) => {
					const changedSection = changedBlockData?.sections[index];
					if (changedSection) {
						const { selected_products, products: changedProducts } =
							changedSection;
						section.products.forEach((product, index) => {
							const changedProduct = changedProducts[index];
							if (changedProduct) {
								const {
									quantity,
									bundled_products: changedBundledProducts
								} = changedProduct;
								product?.bundled_products?.forEach(
									(bundledProduct, index) => {
										const changedBundledProduct =
											changedBundledProducts[index];
										if (changedBundledProduct) {
											bundledProduct.quantity =
												changedBundledProduct.quantity;
										}
									}
								);
								product.quantity = quantity;
							}
						});
						section.selected_products = selected_products;
					}
				});
			}
		});
	}
	if (document.payment && documentData?.payment) {
		document.payment = documentData?.payment;
	}
};

export interface DocumentCurrencyConfigViewWire {
	currency?: { code: string; display_name?: string; displayName?: string };
	frozen_exchange_rate?: number;
	frozenExchangeRate?: number;
	frozen_at?: string;
	frozenAt?: string;
}

/**
 * The document GET response serializes currency_config in snake_case
 * (`frozen_exchange_rate`, `currency.display_name`) and is NOT passed through
 * any wire-mapping, unlike the company-level config from the Settings service.
 * Consumers read the camelCase `DocumentCurrencyConfigView` shape, so the
 * frozen rate would silently read as `undefined`. Normalize at the single
 * document-response ingestion point so runtime matches the declared type.
 */
export const normalizeDocumentCurrencyConfig = (
	raw: DocumentCurrencyConfigViewWire | null | undefined
): DocumentCurrencyConfigView | null => {
	if (!raw) return null;
	return {
		currency: {
			code: raw.currency?.code ?? "",
			displayName:
				raw.currency?.displayName ?? raw.currency?.display_name ?? ""
		},
		frozenExchangeRate: raw.frozenExchangeRate ?? raw.frozen_exchange_rate,
		frozenAt: raw.frozenAt ?? raw.frozen_at
	};
};

export const withNormalizedDocumentCurrencyConfig = (
	document: Document
): Document =>
	document.currency_config
		? {
				...document,
				currency_config: normalizeDocumentCurrencyConfig(
					document.currency_config as unknown as DocumentCurrencyConfigViewWire
				)
		  }
		: document;

export const concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse =
	(document: Document): Document => {
		const newDocument: Document = withNormalizedDocumentCurrencyConfig({
			...document
		});
		if (!newDocument.revisions) return newDocument;
		const revisions = [...(newDocument.revisions ?? [])];
		revisions.forEach(revision => {
			if (revision.blocks || revision.draft_blocks) {
				const data = [
					...(revision.blocks?.data ?? []),
					...(revision.draft_blocks?.data ?? [])
				];
				revision.blocks = { data };
				delete revision.draft_blocks;
			}
			if (revision.block_list || revision.draft_block_list) {
				const blockList = [
					...revision.block_list,
					...(revision.draft_block_list ?? [])
				];
				revision.block_list = blockList;
				delete revision.draft_block_list;
			}
		});
		newDocument.revisions = revisions;
		return newDocument;
	};

export const getPaperSizeDimensions = (size: DocumentPaperSize): string => {
	switch (size) {
		case DocumentPaperSize.A4:
			return '8.27" x 11.69"';
		case DocumentPaperSize.Letter:
			return '8.5" x 11"';
		default:
			return "";
	}
};
